import * as dayjs from 'dayjs';

export interface RecurringSchedule {
  type: 'weekly' | 'bi-weekly' | 'monthlyNthDay';
  endDate?: string;
}

interface RecurringEventProperties {
  startDate: string;
  recurringSchedule: RecurringSchedule;
}

const getNthDayOfMonth = (date: string | Date, day: number, nthWeek: number): string => {
  const firstDayOfMonth = dayjs(date).startOf('month');

  // Calculate the first occurrence of the desired weekday in the month
  let firstOccurrence = firstDayOfMonth.day(day);

  // Adjust for cases where the first occurrence rolls back to the previous month
  if (firstOccurrence.month() !== firstDayOfMonth.month()) {
    firstOccurrence = firstOccurrence.add(7, 'day');
  }

  // Calculate the nth occurrence of the weekday
  const nthOccurrence = firstOccurrence.add((nthWeek - 1) * 7, 'day');

  return nthOccurrence.format('YYYY-MM-DD');
};

const getNextRecurringEventStartDate = (
  event: RecurringEventProperties,
  referenceDate?: string,
): string | undefined => {
  if (!event.recurringSchedule) {
    return undefined;
  }

  const now = referenceDate ? dayjs(referenceDate) : dayjs();
  const startDate = dayjs(event.startDate);
  const day = startDate.day();

  if (now.isBefore(startDate)) {
    return undefined;
  }

  if (event.recurringSchedule.type === 'weekly') {
    if (now.day() === day) {
      return now.format('YYYY-MM-DD');
    }

    let nextDay = now.day(day);
    if (nextDay.isBefore(now)) {
      nextDay = nextDay.add(1, 'week');
    }

    return nextDay.format('YYYY-MM-DD');
  }

  if (event.recurringSchedule.type === 'bi-weekly') {
    const weeksSinceStart = Math.floor(now.diff(startDate, 'week') / 2);
    let nextDay = startDate.add(weeksSinceStart * 2, 'week');

    if (nextDay.isBefore(now)) {
      nextDay = nextDay.add(2, 'week');
    }

    return nextDay.format('YYYY-MM-DD');
  }

  if (event.recurringSchedule.type === 'monthlyNthDay') {
    const nthWeekForDay = Math.ceil(startDate.date() / 7);

    const nextDay = getNthDayOfMonth(now.toDate(), day, nthWeekForDay);

    if (dayjs(nextDay).isBefore(now)) {
      return getNthDayOfMonth(now.add(1, 'month').toISOString(), day, nthWeekForDay);
    }

    return nextDay;
  }

  return undefined;
};

export const getNextRecurringEventDate = (
  event: RecurringEventProperties,
  referenceDate?: string,
): string | undefined => {
  const nextStartDate = getNextRecurringEventStartDate(event, referenceDate);
  if (!nextStartDate) {
    return undefined;
  }
  if (
    event.recurringSchedule?.endDate &&
    dayjs(nextStartDate).isAfter(dayjs(event.recurringSchedule.endDate))
  ) {
    return undefined;
  }
  return nextStartDate;
};
